import {useHistoryPageState} from '@aboutyou/history-page-state';
import {useTranslation} from '@aboutyou/i18n';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {styled} from '@linaria/react';
import React, {
  ComponentType,
  CSSProperties,
  useEffect,
  useRef,
  useState,
} from 'react';
import {COLORS} from '../../../constants/colors';
import {useAutomaticSlide} from '../../../hooks/useAutomaticSlide/useAutomaticSlide';
import {UnstyledButton} from '../../atoms/ButtonsDeprecated/UnstyledButton';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {useSlider} from '../../molecules/HorizontalSlider/hooks/useSlider';
import {SliderHookResponse} from '../../molecules/HorizontalSlider/types';
import {SnapScrollSlider} from '../../molecules/SnapScrollSlider/SnapScrollSlider';

export interface SpotlightSliderProps<Slide> {
  className?: string;
  style?: CSSProperties;
  slides: Slide[];
  slideDurationInSecs?: number;
  uniqueKey?: string;
  isInitialAutoplayStopped?: boolean;
  showNavigationButtons?: boolean;
  CardComponent: ComponentType<
    Slide & {
      isCloseToViewport: boolean;
      'data-testid'?: string;
      isActive: boolean;
      index: number;
      currentSlide?: number;
      maxPages?: number;
    }
  >;
  BackgroundComponent?: ComponentType<
    Slide & {
      'data-testid'?: string;
      isActive: boolean;
    }
  >;
  BottomPaginationComponent?: ComponentType<
    Pick<SliderHookResponse, 'currentSlide' | 'scrollToSlide'> & {
      maxPages: number;
      isAnimationStopped: boolean;
      isAnimationPaused: boolean;
      autoplayDuration: number;
      onStopAutomaticSlide: VoidFunction;
    }
  >;
}

const AUTO_SLIDE_DURATION_IN_SECS = 5;

export const SpotlightSlider = <Slide extends object>({
  className,
  slides,
  CardComponent,
  BottomPaginationComponent,
  slideDurationInSecs = AUTO_SLIDE_DURATION_IN_SECS,
  BackgroundComponent,
  style,
  uniqueKey,
  isInitialAutoplayStopped,
  showNavigationButtons,
}: SpotlightSliderProps<Slide>) => {
  const {t} = useTranslation();
  const isInitialMount = !useHydrationDone();
  const sliderRef = useRef<HTMLUListElement>(null);
  const [activeBrandIndex, setActiveBrandIndex] = useHistoryPageState(0, {
    uniqueKey: uniqueKey || 'default_spotlight_slider',
  });
  const {currentSlide, scrollToSlide, scrollToPrevItem, scrollToNextItem} =
    useSlider(sliderRef);
  const [navBorderColor, setNavBorderColor] = useState<string>();
  const {
    onStop: onStopAutomaticSlide,
    onPause: onPauseAutomaticSlide,
    onResume: onResumeAutomaticSlide,
    onReset: onResetAutomaticSlide,
    animationPaused,
    animationStopped,
  } = useAutomaticSlide({
    slidesLength: slides.length,
    currentSlide,
    scrollToSlide,
    slideDurationInSecs,
    stoppedInitialState: isInitialAutoplayStopped,
  });

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setActiveBrandIndex(currentSlide);
  }, [currentSlide, setActiveBrandIndex]);

  useEffect(() => {
    if (!isInitialAutoplayStopped) {
      onResetAutomaticSlide();
    }
  }, [isInitialAutoplayStopped]);

  useEffect(() => {
    if (!uniqueKey || isInitialAutoplayStopped) {
      return;
    }

    scrollToSlide(activeBrandIndex, {behavior: 'auto'});
    onResetAutomaticSlide();
  }, [uniqueKey, isInitialAutoplayStopped]);

  const handleOnClickPrev = () => {
    onStopAutomaticSlide();
    scrollToPrevItem();
  };

  const handleOnClickNext = () => {
    onStopAutomaticSlide();
    scrollToNextItem();
  };

  return (
    <SliderContainer
      className={className}
      style={style}
      data-testid="spotlightSlider"
    >
      <BackgroundImagesWrapper>
        {BackgroundComponent &&
          slides.map((slide, index) => (
            <BackgroundComponent
              key={`ambient-slider-background-image${index}`}
              {...slide}
              data-testid={`background-card-${index}${
                index === currentSlide ? '-active' : ''
              }`}
              isActive={index === currentSlide}
            />
          ))}
      </BackgroundImagesWrapper>
      <SlidesContainer ref={sliderRef}>
        {slides.map((slide, index) => {
          // On the initial mount we only want to prioritize the first image, as it's the highest priority on the server.
          // In the client we have enough time to prioritize the first two images to preload what the user will most probably unfold soon.
          // Logic and comment are taken from the product images on ADP.
          const isCloseToViewport = isInitialMount
            ? index === currentSlide
            : currentSlide - 1 <= index && currentSlide + 1 >= index;

          return (
            <li
              key={`spotlightSlider-slide-${index}`}
              data-testid={`card-${index}${
                index === currentSlide ? '-active' : ''
              }`}
            >
              <CardComponent
                {...slide}
                isCloseToViewport={isCloseToViewport}
                isActive={index === currentSlide}
                onPauseAutomaticSlide={onPauseAutomaticSlide}
                onResumeAutomaticSlide={onResumeAutomaticSlide}
                onStopAutomaticSlide={onStopAutomaticSlide}
                index={index}
                currentSlide={currentSlide}
                maxPages={slides.length}
                scrollToSlide={scrollToSlide}
                isAnimationStopped={animationStopped}
                isAnimationPaused={animationPaused}
                setNavBorderColor={setNavBorderColor}
                uniqueKey={uniqueKey}
              />
            </li>
          );
        })}
      </SlidesContainer>
      {showNavigationButtons && (
        <NavigationButtonsWrapper>
          <ControlButton
            aria-label={t('Go to the previous slide')}
            onClick={handleOnClickPrev}
            disabled={currentSlide === 0}
            data-testid="previous-card-button"
            $navBorderColor={navBorderColor}
          >
            <StyledIcon asset={GenericIconName.ArrowLeft} size="XS" />
          </ControlButton>
          <ControlButton
            aria-label={t('Go to the next slide')}
            onClick={handleOnClickNext}
            disabled={currentSlide === slides.length - 1}
            data-testid="next-card-button"
            $navBorderColor={navBorderColor}
          >
            <StyledIcon asset={GenericIconName.ArrowRight} size="XS" />
          </ControlButton>
        </NavigationButtonsWrapper>
      )}
      {slides.length > 1 && BottomPaginationComponent && (
        <div key={uniqueKey}>
          <BottomPaginationComponent
            currentSlide={currentSlide}
            scrollToSlide={scrollToSlide}
            maxPages={slides.length}
            isAnimationStopped={animationStopped}
            isAnimationPaused={animationPaused}
            autoplayDuration={slideDurationInSecs}
            onStopAutomaticSlide={onStopAutomaticSlide}
          />
        </div>
      )}
    </SliderContainer>
  );
};

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
`;

const BackgroundImagesWrapper = styled.ul`
  position: absolute;
  inset: 0;
  list-style: none;
`;

export const SlidesContainer = styled.ul`
  ${SnapScrollSlider}
`;

const NavigationButtonsWrapper = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  margin: 16px;
  display: flex;
  z-index: 2;
`;

const ControlButton = styled(UnstyledButton)<{$navBorderColor?: string}>`
  --nav-border-color: ${props =>
    props.$navBorderColor ? props.$navBorderColor : COLORS['Outline.Grey']};
  --nav-icon-color: ${props =>
    props.$navBorderColor ? props.$navBorderColor : COLORS['Text.Black']};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: rgba(255, 255, 255, 0.4);
  border: 1px solid var(--nav-border-color);
  backdrop-filter: blur(8px);
  cursor: unset;
  transition: background-color 250ms ease-in;

  svg {
    transition: fill 350ms ease-in;
  }

  &:disabled svg {
    fill: ${COLORS['Fill.Grey.Inactive']};
  }

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
  }

  &:first-child {
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
    border-right: none;
  }

  &:last-child {
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;
  }
`;

const StyledIcon = styled(Icon)`
  fill: var(--nav-icon-color);
`;
